import { RefObject, useEffect, useState } from 'react';

import { useGesture } from '@use-gesture/react';

import { getLaneFromPosition } from '../helpers/viewportFunctions';
import { LengthRange } from '../types/lengthRange';

export enum CoreLaneElementType {
	Image,
	HorizontalRuler,
}

export interface CoreLaneHoverLocation {
	/*
	 * x,y position of the mouse cursor relative to the viewport
	 */
	viewPortPositionInPx?: [number, number];
	/*
	 * Position across the hover lane horizontal (in m) that is being hovered on, relative to DH start
	 */
	atDepthInM?: number;
	/*
	 * Type of element under the mouse cursor
	 */
	elementType?: CoreLaneElementType;
}

export interface UseHoverLocationParams<T extends Element> {
	target?: RefObject<T>;
	lanes?: LengthRange[];
	// TODO: setHoverLaneRange must be removed here and `laneRange` added ro `CoreLaneHoverLocation`. ATM it's cause a circular dependency
	setHoverLaneRange: (lane: LengthRange | undefined) => void;
	getLaneYLocation: (lane: LengthRange) => number | undefined;
	laneHeight: number;
	pxPerM: number;
	horizontalRulerHeight?: number;
}

export const useHoverLocation = <T extends Element>(props: UseHoverLocationParams<T>): CoreLaneHoverLocation => {
	const [viewPortPositionInPx, setViewPortPositionInPx] = useState<[number, number]>();
	const [hoverLocation, setHoverLocation] = useState<CoreLaneHoverLocation>({});

	const { target, lanes, setHoverLaneRange, getLaneYLocation, laneHeight, pxPerM, horizontalRulerHeight } = props;

	useEffect(() => setHoverLaneRange(undefined), [setHoverLaneRange]);

	useGesture(
		{
			onMouseMove: (state) => {
				// Calculate mouse move from page X/Y, as offsets would trigger for children giving bad values;
				const targetRect = target?.current?.getBoundingClientRect();
				if (!targetRect) return;

				setViewPortPositionInPx([state.event.pageX - targetRect.x, state.event.pageY - targetRect.y]);
			},
			onMouseLeave: () => setViewPortPositionInPx(undefined),
		},
		{
			target,
		},
	);

	useEffect(() => {
		if (!viewPortPositionInPx || !lanes) {
			setHoverLaneRange(undefined);
			setHoverLocation({});
			return;
		}
		const [x, y] = viewPortPositionInPx;
		const lane = getLaneFromPosition(lanes, getLaneYLocation, laneHeight, y);

		if (!lane) {
			setHoverLaneRange(undefined);
			setHoverLocation({ viewPortPositionInPx: viewPortPositionInPx });
			return;
		}

		let elementType = CoreLaneElementType.Image;
		if (horizontalRulerHeight !== undefined) {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const laneY = getLaneYLocation(lane)!;
			if (y > laneY + laneHeight - horizontalRulerHeight) elementType = CoreLaneElementType.HorizontalRuler;
		}

		const at = lane.depthFrom + x / pxPerM;
		setHoverLaneRange(lane);
		setHoverLocation({
			viewPortPositionInPx: viewPortPositionInPx,
			atDepthInM: at,
			elementType,
		});
	}, [viewPortPositionInPx, getLaneYLocation, lanes, pxPerM, setHoverLaneRange, laneHeight, horizontalRulerHeight]);

	return hoverLocation;
};
